/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Form,
  Button,
  Drawer,
  Card,
  Flex,
  Space,
  Avatar,
  Typography,
  Spin,
  Input,
} from 'antd';
import InputFormBox from 'components/bank/AGBBank/InputFormBox';
import NoComponent from 'components/bank/AGBBank/NoComponent';
import FormItemInput from 'components/shared/FormItemInput';
import TransferConfirmation from 'components/shared/TransferConfirmation';
import { filteredFormInputItems } from 'constants/InputItems/agbInputItems';
import { LocationState } from 'interfaces/otherInterface/dynamicInput';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { IConfirmationData } from 'interfaces/queries/transactions';
import { ITransferForm } from 'interfaces/components/pageProps';
import AccountDropdown from 'components/shared/AccountDropdown';
import { CloseOutlined } from '@ant-design/icons';
import { useLanguage } from 'hooks/Language/useLanguage';
import { DrawerStyles } from 'antd/es/drawer/DrawerPanel';
import agbColors from 'constants/Colors/agbColors';
import { maxSize } from 'constants/screenSize';
import ContactDropdown, {
  ImageWithFallback,
} from 'components/shared/ContactDropdown';

import OssixProvidersDropdown from 'components/shared/OssixProvidersDropdown';
import { ActionType, TargetType } from 'types/generalType';
import VerificationModal from 'components/bank/AGBBank/VerificationModal/VerificationModal';

const { Text } = Typography;

const BetweenOwnAccount: React.FC<ITransferForm> = ({
  form,
  step,
  onPrevious,
  accountNumber,
  setAccountNumber,
  loadingAccount,
  selectAccountNumber,
  dynamicFormItem,
  handleCloseOpenDrawer,
  isDrawerOpen,
  onHandleDisplaySelectContact,
  displaySelectContact,
  contactAccount,
  setContactAccount,
  isLoadingContacts,
  onHandleCloseSelectContact,
  recipientAccountNumber,
  setSelectedProvider,
  showVerificationModal,
  verificationData,
  handleVerificationComplete,
  handleVerificationCancel,
}) => {
  const { translate } = useLanguage();
  const location = useLocation();
  const state = location.state as LocationState;

  const {
    transferConfirmationData,
    onPINValuesChange,
    onHandleConfirmTransfer,
    loadSendFundsToGateway,
    onHandleClosePrintPage,
    selectedBeneficiary,
    onHandleValueChange,
    formData,
    onHandleTransferConfirmation,
    loadTransferConfirmation,
    selectCountry,
    userData,
    selectedProvider,
    setSelectedBeneficiary,
    secondAccountNumber,
    setSecondAccountNumber,
  } = dynamicFormItem;

  const formItems = useMemo(
    () =>
      filteredFormInputItems(['Amount']).map((item, index) => (
        <FormItemInput
          key={index}
          label={item.label}
          placeholder={item.placeholder}
          name={item.name}
          disabled={item.disabled}
        />
      )),
    [],
  );

  const convertConfirmationData = (data: IConfirmationData) => {
    return [
      { label: 'Amount to be sent', value: data?.Amount },
      { label: 'Fees', value: data?.Fees },
      { label: 'Exchange fees', value: data?.ExchangeFees },
      { label: 'Exchange rate', value: data?.ExchangeRate },
      { label: 'Taxes', value: data?.Taxes },
      { label: 'Total amount', value: data?.TotalAmount },
    ];
  };

  const confirmationItems = convertConfirmationData(
    transferConfirmationData,
  );

  const drawerStyles: DrawerStyles = {
    header: {
      background: agbColors.navyBlueColor,
      color: agbColors.lightBlueColor,
    },
  };

  const renderStep1Actions = () => (
    <div className="drawer-footer">
      <Button
        type="primary"
        onClick={onHandleTransferConfirmation}
        loading={loadTransferConfirmation}
        size="large"
        block
      >
        {translate('Proceed')}
      </Button>
    </div>
  );

  const renderFooter = () => {
    switch (step) {
      case 1:
        return renderStep1Actions();
      case 2:
        return (
          <div className="drawer-footer dual-button">
            <Button
              variant="filled"
              color="volcano"
              type="primary"
              onClick={onPrevious}
              size="large"
            >
              {translate('Back')}
            </Button>
            <Button
              type="primary"
              onClick={onHandleConfirmTransfer}
              loading={loadSendFundsToGateway}
              size="large"
            >
              {translate('Confirm')}
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="drawer-scrollable-content">
            <InputFormBox
              title={state?.chosenOption}
              onSelectRecipient={onHandleDisplaySelectContact}
              displaySelectRecipient={
                displaySelectContact ? false : true
              }
              select={[
                <Form
                  layout="vertical"
                  style={{ marginBottom: '-10px' }}
                >
                  {accountNumber?.value !== 'undefined' && (
                    <Form.Item label={translate('Transfer from')}>
                      <AccountDropdown
                        account={accountNumber}
                        loading={loadingAccount}
                        options={selectAccountNumber}
                        setAccount={setAccountNumber}
                        size="100%"
                      />
                    </Form.Item>
                  )}
                </Form>,
              ]}
              formItems={[
                <Form
                  form={form}
                  layout="vertical"
                  key="form"
                  autoComplete="off"
                  className="custom-input-margin__bottom"
                  onValuesChange={onHandleValueChange}
                  initialValues={{ ...formData, remembered: true }}
                >
                  {!displaySelectContact && (
                    <Form.Item label={translate('Transfer to')}>
                      <AccountDropdown
                        account={secondAccountNumber}
                        loading={loadingAccount}
                        options={selectAccountNumber}
                        setAccount={setSecondAccountNumber}
                        size="100%"
                      />
                    </Form.Item>
                  )}

                  {displaySelectContact && (
                    <Flex
                      align="center"
                      style={{ width: '100%', marginTop: '-15px' }}
                      vertical
                    >
                      <Space.Compact style={{ width: '100%' }}>
                        <Form.Item
                          label={translate('Select a beneficiary')}
                          className="custom-input-margin__bottom"
                          name="recipient"
                          style={{ width: '100%' }}
                        >
                          <ContactDropdown
                            contact={contactAccount}
                            setContact={setContactAccount}
                            loading={isLoadingContacts}
                            options={recipientAccountNumber}
                          />
                        </Form.Item>
                        <Form.Item label=" ">
                          <Button
                            size="large"
                            type="primary"
                            ghost
                            onClick={onHandleCloseSelectContact}
                            icon={<CloseOutlined />}
                          />
                        </Form.Item>
                      </Space.Compact>
                    </Flex>
                  )}

                  {selectedBeneficiary && (
                    <Card
                      size="small"
                      className="border-grey-color large-margin-bottom"
                      style={{
                        background:
                          agbColors.$lightNayBlueColor10Percent,
                        width: maxSize,
                      }}
                    >
                      <Flex gap="small" align="center">
                        <Avatar
                          size={48}
                          shape="square"
                          src={
                            <ImageWithFallback
                              src={selectedBeneficiary?.Logo}
                              firstName={
                                selectedBeneficiary?.FirstName
                              }
                              lastName={selectedBeneficiary?.LastName}
                            />
                          }
                        />
                        <Flex vertical>
                          <Flex gap="small" align="center">
                            <span className="font-weight-bold">
                              {translate('Name')}:
                            </span>
                            <span className="medium-text">
                              {selectedBeneficiary?.FirstName}{' '}
                              {selectedBeneficiary?.LastName}
                            </span>
                          </Flex>
                          <Flex gap="small" align="center">
                            <span className="font-weight-bold">
                              {translate('Account Number')}:
                            </span>
                            <span className="medium-text">
                              {selectedBeneficiary?.AccountNumber}
                            </span>
                          </Flex>
                        </Flex>
                      </Flex>
                    </Card>
                  )}

                  {!selectedBeneficiary && (
                    <Form.Item
                      label={translate('Select operator')}
                      style={{ display: 'none' }}
                      className="operator-dropdown"
                    >
                      <OssixProvidersDropdown
                        style={{ width: '100%' }}
                        getSelectedProvider={setSelectedProvider}
                        selectedProvider={selectedProvider}
                        beneficiaryData={selectedBeneficiary}
                        setSelectedBeneficiary={
                          setSelectedBeneficiary
                        }
                        onCloseSelectContactDropdown={
                          onHandleCloseSelectContact
                        }
                        payload={{
                          ActionType: ActionType.Transfer,
                          CountryCode:
                            selectCountry?.CountryCode ||
                            userData?.OperationCountry ||
                            userData?.[0]?.OperationCountry,
                          TargetType: TargetType.Bank,
                        }}
                        showDefaultValue={false}
                      />
                    </Form.Item>
                  )}

                  {formItems}

                  <Form.Item className="custom-drawer-footer">
                    <div className="custom-flex">
                      <Button
                        type="primary"
                        onClick={onHandleTransferConfirmation}
                        loading={loadTransferConfirmation}
                      >
                        {translate('Proceed')}
                      </Button>
                    </div>
                  </Form.Item>
                </Form>,
              ]}
            />
          </div>
        );
      case 2:
        return (
          <div className="drawer-scrollable-content">
            <TransferConfirmation
              isLoading={loadSendFundsToGateway}
              items={confirmationItems}
              onHandleBack={onPrevious}
              onHandleConfirm={onHandleConfirmTransfer}
              onValuesChange={onPINValuesChange}
              onHandleClosePrintPage={onHandleClosePrintPage}
              hideButtons={true}
            />
          </div>
        );
      default:
        return (
          <div className="empty-state">
            <NoComponent />
          </div>
        );
    }
  };

  return (
    <Drawer
      width={500}
      open={isDrawerOpen?.open}
      title={
        <Text
          style={{ color: 'white', fontSize: 16, fontWeight: 500 }}
        >
          {translate(isDrawerOpen?.drawerTitle)}
        </Text>
      }
      styles={drawerStyles}
      closeIcon={false}
      onClose={handleCloseOpenDrawer}
      className="transfer-mno-drawer"
      rootClassName="transfer-mno-drawer-root"
      extra={
        <Button
          type="text"
          onClick={handleCloseOpenDrawer}
          icon={<CloseOutlined style={{ color: 'white' }} />}
          className="drawer-close-button"
        />
      }
    >
      {isDrawerOpen?.open ? (
        <div className="drawer-layout-container">
          {renderStep()}
          {renderFooter()}
        </div>
      ) : (
        <Flex
          justify="center"
          align="center"
          style={{ height: '100%' }}
        >
          <Spin size="large" />
        </Flex>
      )}

      <VerificationModal
        isVisible={showVerificationModal}
        otpRequired={verificationData.otpRequired}
        selfieRequired={verificationData.selfieRequired}
        onComplete={handleVerificationComplete}
        onCancel={handleVerificationCancel}
        loading={loadTransferConfirmation}
      />
    </Drawer>
  );
};

export default BetweenOwnAccount;
